import { useEffect, useMemo, useState } from 'react';
import { Bike, RefreshCw, Check, X, Printer } from 'lucide-react';
import type { PedidoVista, PedidoDetalle } from '@shared/types';
import { seLePuedeCobrar } from '@shared/cobroVisible';
import { useNegocioStore } from '@renderer/store/useNegocioStore';
import { useSessionStore } from '@renderer/store/useSessionStore';
import { formatPrecio } from '@renderer/utils/formatters';
import { CobroModal } from '@renderer/components/cobro/CobroModal';
import { TicketPreviewModal } from '@renderer/components/ticket/TicketPreviewModal';
import { AsignarRepartidorModal } from '@renderer/components/despacho/AsignarRepartidorModal';
import { NoEntregadoModal } from '@renderer/components/despacho/NoEntregadoModal';
import { HelpButton } from '@renderer/components/help/HelpButton';
import { tourDespacho } from '@renderer/tours';
/*
  Sprint 111 — Se retira el import huérfano de `traducirErrorBackend`. Esta pantalla salió del menú
  en el Sprint 99 y está marcada para borrarse cuando el tablero de A domicilio lleve un par de
  semanas validado (DT-097). Mientras siga en el árbol, al menos que no cargue lo que no usa.
*/
import { esWeb } from '@renderer/lib/entorno';
import { imprimirDocumento } from '@renderer/lib/impresionNavegador';

/**
 * Sprint 8 — Página de despacho a repartidor.
 *
 * Two columns:
 *  1. LISTOS PARA DESPACHAR — pedidos delivery `estado='listo'`. Acción: asignar repartidor.
 *  2. EN RUTA — pedidos delivery `estado='en_ruta'`. Acción: marcar entregado / no entregado.
 *
 * RN-S8-08: dueña + mesero pueden asignar y marcar entregado.
 *           "No entregado" SOLO dueña con PIN.
 */
export function DespachoPage() {
  const [pedidos, setPedidos] = useState<PedidoVista[]>([]);
  const [cargando, setCargando] = useState(true);
  const [asignarPedido, setAsignarPedido] = useState<PedidoVista | null>(null);
  const [noEntregadoPedido, setNoEntregadoPedido] = useState<PedidoVista | null>(null);
  const [cobroId, setCobroId] = useState<number | null>(null);
  const [pdfPath, setPdfPath] = useState<string | null>(null);
  const [previewId, setPreviewId] = useState<number | null>(null);
  const negocio = useNegocioStore((s) => s.negocio);
  const sesion = useSessionStore((s) => s.sesion);
  const monedaSimbolo = negocio?.monedaSimbolo ?? '$';
  const locale = negocio?.locale ?? 'es-MX';

  const cargar = async () => {
    setCargando(true);
    const res = await window.api.pedidos.listarPorEstado(['listo', 'en_ruta', 'entregado']);
    if (res.ok) {
      setPedidos(res.data.filter((p) => p.tipo === 'delivery'));
    }
    setCargando(false);
  };

  useEffect(() => {
    cargar();
    const t = setInterval(cargar, 15000); // refresco suave cada 15s
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const listos = useMemo(() => pedidos.filter((p) => p.estado === 'listo'), [pedidos]);
  const enRuta = useMemo(() => pedidos.filter((p) => p.estado === 'en_ruta'), [pedidos]);
  const entregadosPendienteCobro = useMemo(
    () => pedidos.filter((p) => p.estado === 'entregado'),
    [pedidos],
  );

  const onMarcarEntregado = async (p: PedidoVista) => {
    const r = await window.api.pedidos.marcarEntregado({ pedidoId: p.id });
    if (!r.ok) {
      window.alertar({ titulo: 'Error', mensaje: r.error, variant: 'warn' });
      return;
    }
    cargar();
    // Si quedó como 'entregado' (no auto-pagado), abrir cobro inmediatamente.
    if (r.data.estado === 'entregado') {
      setCobroId(p.id);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between" data-tour="dsp-header">
        <div className="flex items-center gap-3">
          <Bike className="h-7 w-7 text-primary" />
          <div>
            <h1 className="text-2xl font-bold">Despacho</h1>
            <p className="text-sm text-slate-500">
              {listos.length} listo{listos.length !== 1 ? 's' : ''} · {enRuta.length} en ruta
              {entregadosPendienteCobro.length > 0
                ? ` · ${entregadosPendienteCobro.length} pendiente cobro`
                : ''}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={cargar} className="btn-ghost" aria-label="Refrescar">
            <RefreshCw className="h-4 w-4" />
          </button>
          <HelpButton tour={tourDespacho} />
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        {/* Columna 1: LISTOS PARA DESPACHAR */}
        <div className="card p-0 overflow-hidden" data-tour="dsp-listos">
          <div className="bg-emerald-50 border-b border-emerald-200 px-4 py-2">
            <h2 className="font-semibold text-emerald-800">
              Listos para despachar ({listos.length})
            </h2>
          </div>
          <div className="divide-y divide-slate-100">
            {cargando ? (
              <div className="px-4 py-8 text-center text-slate-400">Cargando…</div>
            ) : listos.length === 0 ? (
              <div className="px-4 py-8 text-center text-slate-400">
                Sin pedidos esperando salida.
              </div>
            ) : (
              listos.map((p) => (
                <CardPedido
                  key={p.id}
                  pedido={p}
                  monedaSimbolo={monedaSimbolo}
                  locale={locale}
                  onPrincipal={() => setAsignarPedido(p)}
                  // Sprint 76 — Lo que se hace aquí es entregarle el paquete a la moto; con el
                  // repartidor ya capturado en la cotización, "asignar" dejó de describirlo.
                  principalLabel={p.nombreRepartidor ? `Entregar a ${p.nombreRepartidor}` : 'Entregar a la moto'}
                  principalColor="bg-emerald-600 hover:bg-emerald-700"
                  onPreview={() => setPreviewId(p.id)}
                />
              ))
            )}
          </div>
        </div>

        {/* Columna 2: EN RUTA */}
        <div className="card p-0 overflow-hidden" data-tour="dsp-en-ruta">
          <div className="bg-blue-50 border-b border-blue-200 px-4 py-2">
            <h2 className="font-semibold text-blue-800">En ruta ({enRuta.length})</h2>
          </div>
          <div className="divide-y divide-slate-100">
            {enRuta.length === 0 ? (
              <div className="px-4 py-8 text-center text-slate-400">
                Sin repartidores en ruta.
              </div>
            ) : (
              enRuta.map((p) => (
                <div key={p.id} className="p-4 space-y-2">
                  <CardPedidoCompacto
                    pedido={p}
                    monedaSimbolo={monedaSimbolo}
                    locale={locale}
                  />
                  <div className="flex gap-2">
                    <button
                      onClick={() => onMarcarEntregado(p)}
                      className="btn-primary px-3 py-2 text-xs flex-1 bg-blue-600 hover:bg-blue-700"
                    >
                      <Check className="h-4 w-4" />
                      Entregado
                    </button>
                    {sesion?.rol === 'dueño' && (
                      <button
                        onClick={() => setNoEntregadoPedido(p)}
                        className="btn-ghost px-3 py-2 text-xs text-rose-700 border border-rose-200"
                      >
                        <X className="h-4 w-4" />
                        No entregado
                      </button>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Pendientes de cobro post-entrega */}
      {entregadosPendienteCobro.length > 0 && (
        <div className="card p-0 overflow-hidden">
          <div className="bg-amber-50 border-b border-amber-200 px-4 py-2">
            <h2 className="font-semibold text-amber-800">
              Entregados — pendientes de cobro ({entregadosPendienteCobro.length})
            </h2>
          </div>
          <div className="divide-y divide-slate-100">
            {entregadosPendienteCobro.map((p) => (
              <div key={p.id} className="p-4 flex items-center justify-between gap-4">
                <CardPedidoCompacto
                  pedido={p}
                  monedaSimbolo={monedaSimbolo}
                  locale={locale}
                />
                {/*
                  Sprint 88 — Misma regla que en Pedidos: no se ofrece cobrar lo que ya se pagó.

                  Aquí el caso normal ya estaba cubierto (un domicilio pagado por transferencia
                  pasa a 'pagado' al entregarse, no a 'entregado'), pero la lista se arma sólo por
                  estado. Si un pedido llega aquí con su pago hecho, el botón invitaría a cobrarlo
                  otra vez — y el que paga dos veces es el cliente, no la base de datos.
                */}
                {seLePuedeCobrar(p) ? (
                  <button
                    onClick={() => setCobroId(p.id)}
                    className="btn-primary px-3 py-2"
                  >
                    Cobrar {formatPrecio(p.total, monedaSimbolo, locale)}
                  </button>
                ) : (
                  <span className="whitespace-nowrap rounded-full bg-emerald-50 px-3 py-2 text-sm font-medium text-emerald-700">
                    ✓ Ya pagado
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <AsignarRepartidorModal
        pedido={asignarPedido}
        onClose={() => setAsignarPedido(null)}
        onAsignado={(pedido, ruta) => {
          setAsignarPedido(null);
          setPdfPath(ruta);
          // Sprint 56.1 — En web el PDF vive en el servidor y `abrirPdf`
          // (shell.openPath) no puede abrirlo en la máquina del cajero: la
          // orden de entrega se imprime desde el navegador.
          if (esWeb()) {
            void imprimirDocumento({ tipo: 'despacho', pedidoId: pedido.id });
          } else if (ruta) {
            // Abrir el PDF de despacho automáticamente.
            window.api.ticket.abrirPdf(ruta);
          }
          cargar();
        }}
      />

      <NoEntregadoModal
        pedido={noEntregadoPedido}
        onClose={() => setNoEntregadoPedido(null)}
        onConfirmado={() => {
          setNoEntregadoPedido(null);
          cargar();
        }}
      />

      <CobroModal
        abierto={cobroId !== null}
        pedidoId={cobroId}
        onClose={() => setCobroId(null)}
        onCobrado={() => {
          setCobroId(null);
          cargar();
        }}
      />

      <TicketPreviewModal
        abierto={previewId !== null}
        pedidoId={previewId}
        pdfPath={pdfPath}
        onClose={() => {
          setPreviewId(null);
          setPdfPath(null);
        }}
      />
    </div>
  );
}

function CardPedido(props: {
  pedido: PedidoVista;
  monedaSimbolo: string;
  locale: string;
  onPrincipal: () => void;
  principalLabel: string;
  principalColor: string;
  onPreview?: () => void;
}) {
  const { pedido: p, monedaSimbolo, locale, onPrincipal, principalLabel, principalColor, onPreview } = props;
  const [detalle, setDetalle] = useState<PedidoDetalle | null>(null);
  useEffect(() => {
    let alive = true;
    window.api.pedidos.obtener(p.id).then((r) => {
      if (alive && r.ok) setDetalle(r.data);
    });
    return () => {
      alive = false;
    };
  }, [p.id]);
  return (
    <div className="p-4 space-y-2">
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="font-mono font-bold text-lg">
            #{String(p.numeroPedido).padStart(5, '0')}
          </div>
          <div className="text-sm text-slate-700">{p.clienteNombre ?? '—'}</div>
        </div>
        <div className="text-right">
          <div className="font-mono font-bold">{formatPrecio(p.total, monedaSimbolo, locale)}</div>
          <div
            className={`text-xs ${
              p.estadoPago === 'pagado' ? 'text-emerald-700' : 'text-rose-700 font-bold'
            }`}
          >
            {p.estadoPago === 'pagado' ? '✓ Pagado' : `Cobrar al entregar`}
          </div>
        </div>
      </div>
      {detalle?.clienteTelefono && (
        <div className="text-sm font-bold text-slate-800">☎ {detalle.clienteTelefono}</div>
      )}
      {/*
        Sprint 66 — Paso 7 visible para quien llama al repartidor. Sin esto, la caja tiene que
        asomarse a la cocina para saber si ya puede pedirle que venga.
      */}
      {p.fechaEmpaquetado ? (
        <div className="text-xs font-medium text-emerald-700">
          📦 Empaquetado{' '}
          {new Date(p.fechaEmpaquetado).toLocaleTimeString('es-MX', {
            hour: '2-digit',
            minute: '2-digit',
          })}
        </div>
      ) : (
        <div className="text-xs text-amber-700">📦 La cocina aún no lo empaqueta</div>
      )}
      {detalle?.clienteDireccion && (
        <div className="text-xs text-slate-600 line-clamp-2">📍 {detalle.clienteDireccion}</div>
      )}
      <div className="flex gap-2 pt-1">
        <button onClick={onPrincipal} className={`btn-primary px-3 py-2 text-xs flex-1 ${principalColor}`}>
          <Printer className="h-4 w-4" />
          {principalLabel}
        </button>
        {onPreview && (
          <button onClick={onPreview} className="btn-ghost px-3 py-2 text-xs">
            Ver ticket
          </button>
        )}
      </div>
    </div>
  );
}

function CardPedidoCompacto({
  pedido: p,
  monedaSimbolo,
  locale,
}: {
  pedido: PedidoVista;
  monedaSimbolo: string;
  locale: string;
}) {
  const fechaSalida = p.fechaSalida ? new Date(p.fechaSalida) : null;
  const minutos = fechaSalida
    ? Math.floor((Date.now() - fechaSalida.getTime()) / 60000)
    : null;
  return (
    <div className="flex-1">
      <div className="flex items-center justify-between gap-2">
        <div className="font-mono font-bold">#{String(p.numeroPedido).padStart(5, '0')}</div>
        <div className="font-mono text-sm">{formatPrecio(p.total, monedaSimbolo, locale)}</div>
      </div>
      <div className="text-sm text-slate-700">{p.clienteNombre ?? '—'}</div>
      {p.nombreRepartidor && (
        <div className="text-xs text-slate-600">
          Repartidor: <span className="font-semibold">{p.nombreRepartidor}</span>
          {minutos !== null && minutos >= 0 ? ` · hace ${minutos} min` : ''}
        </div>
      )}
    </div>
  );
}
